<template>
	<v-main class="fill-height">
		<dockview-vue
			v-if="!assetManagementIsOpen"
			id="dockview-container"
			:class="{alternateColors: isColorCircling}"
			single-tab-mode="default"
			right-header-actions-component="RightActionDockview"
			:theme="theme"
			@ready="onReady"
		/>
		<AssetManagement v-else />
	</v-main>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import {DockviewVue, themeReplit, themeAbyssSpaced} from "dockview-vue";
import emitter from "@/emitter";

/* Modules */
import AssetManagement from "@/components/standaloneModules/AssetManagement.vue";
import Calendar from "@/components/Calendar.vue";
import GoLive from "@/components/GoLive.vue";
import HotKeys from "@/components/HotKeys.vue";
import Library from "@/components/Library.vue";
import StationLog from "@/components/StationLog.vue";
import OnAir from "@/components/OnAir.vue";
import Playlist from "@/components/Playlist.vue";
import TrafficLog from "@/components/TrafficLog.vue";
import MidiUI from "@/components/MidiUI.vue";
import RightActionDockview from "@/components/RightActionDockview.vue";

/* Enums */
import Panels from "@/enums/Panels";
import {getCookie, setCookie} from "@/plugins/cookies";

export default {
	name: "Main",

	components: {
		AssetManagement,
		Calendar,
		DockviewVue,
		GoLive,
		HotKeys,
		Library,
		StationLog,
		OnAir,
		Playlist,
		TrafficLog,
		MidiUI,
		RightActionDockview
	},

	props: ["resetLogoutTimer", "inactiveUser", "inactivityMessage"],

	data() {
		return {
			isPopoutPanel: false,
			isColorCircling: false
		};
	},

	computed: {
		liveModeSelectedStation() {
			return this.$store.getters.liveModeSelectedStation();
		},

		orgID() {
			return this.$store.getters.getOrgID();
		},

		theme() {
			return this.$store.getters.darkMode() ? themeAbyssSpaced : themeReplit;
		},

		playerLoudness() {
			return this.$store.getters.getPlayerLoudness();
		},

		stationIsSilent() {
			return this.$store.getters.getStationIsSilent();
		},

		clientId() {
			return this.$store.getters.clientId();
		},

		assetManagementIsOpen() {
			return this.$store.getters.assetManagementIsOpen();
		},

		liveMode() {
			return this.$store.getters.liveMode();
		}
	},

	watch: {
		liveMode(inLiveMode) {
			this.dockViewApi.clear();
			if (inLiveMode) {
				this.renderLiveMode();
			} else {
				this.renderOfflineMode();
			}
		},

		liveModeSelectedStation(station, previous) {
			if (station && station !== previous) {
				this.dockViewApi.panels.forEach(p => {
					p.api.setTitle(`${Panels[p.params.panelName].title} - ${station.StationName}`);
				});
			}
		},

		playerLoudness(loudness) {
			this.isColorCircling = loudness < -50;
		},

		stationIsSilent(isSilent) {
			this.isColorCircling = isSilent;
		}
	},

	created() {
		// Detect if this window is a popout
		this.isPopoutPanel = !!new URLSearchParams(window.location.search).get("panel");
	},

	mounted() {
		this.mainMidiBC = new BroadcastChannel(`main-midi-channel-${this.clientId}`);
		this.mainMidiBC.addEventListener("message", message => {
			// This window is a popped-out panel (e.g. the MIDI UI opened in its own browser
			// window). It doesn't hold the real WebRTC connection/store, so it must not process
			// broadcast messages meant for the main window (they would crash on an uninitialized
			// nebulaAdapter). Just ignore them here; the main window handles them.
			if (this.isPopoutPanel) {
				return;
			}

			switch (message.data.type) {
				case "playPot":
					this.$store.dispatch(message.data.type, {
						state: message.data.state,
						channel: message.data.channel
					});
					break;
				case "toggleUserMic":
					this.$store.dispatch(message.data.type, {
						on: message.data.on,
						user: message.data.user
					});
					break;
				case "toggleUserAirState":
					this.$store.dispatch(message.data.type, {
						on: message.data.on,
						user: message.data.user
					});
					break;
				case "setGainMaster":
					this.$store.dispatch(message.data.type, message.data.gain);
					break;
				case "setGainChannel":
					this.$store.dispatch(message.data.type, {
						gain: message.data.gain,
						channel: message.data.channel
					});
					break;
				case "setGainMic":
					this.$store.dispatch(message.data.type, {
						gain: message.data.gain,
						user: message.data.user
					});
					break;
				case "dumpButtonPressed":
					emitter.emit("dump:pressed");
					break;
				case "editStudioSettings":
					this.$store.dispatch(message.data.type, JSON.parse(message.data.payload));
					break;
				case "midiWindowClosed":
					emitter.emit("midiWindowClosed");
					break;
			}
		});
	},

	beforeUnmount() {
		emitter.off("docking:openPanel", this.addPanel);
		emitter.off("docking:replacePanel", this.replacePanel);
		emitter.off("docking:replacePanel", this.closePanel);

		this.mainMidiBC.close();
	},

	methods: {
		onReady(event) {
			this.dockViewApi = event.api;

			// If this is a popout panel, restore its previous state from sessionStorage
			// using the panel ID from the URL, add it back to the dock, and skip rendering the main layout.
			if (this.isPopoutPanel) {
				const panelID = new URLSearchParams(window.location.search).get("panel");
				const savedPanelState = sessionStorage.getItem(`popoutPanel-${panelID}`);
				if (savedPanelState) {
					const panel = JSON.parse(savedPanelState);
					this.dockViewApi.addPanel({
						id: panel.id,
						component: panel.component,
						title: panel.params?.panelName ?? panel.component,
						params: panel.params
					});
				}
				return; // skip main layout rendering
			}

			if (this.liveMode) {
				this.renderLiveMode();
			} else {
				this.renderOfflineMode();
			}

			emitter.on("docking:openPanel", this.addPanel);
			emitter.on("docking:replacePanel", this.replacePanel);
			emitter.on("docking:closePanel", this.closePanel);

			this.dockViewApi.onDidRemovePanel(panel => {
				if (panel.params?.panelName === "midi") {
					emitter.emit("midiWindowClosed");
				}
			});

			this.dockViewApi.onDidLayoutChange(() => {
				this.cleanUpPopoutPanelsFromMainWindowSessionStorage();
				const layout = this.dockViewApi.toJSON();
				if (this.liveMode) {
					setCookie(
						"liveModeLayout",
						JSON.stringify({
							layout,
							selectedStation: _.pick(this.liveModeSelectedStation, [
								"StationID",
								"OrganizationID",
								"StationName"
							])
						}),
						3650
					);
				} else {
					setCookie("offlineModeLayout", JSON.stringify({layout}), 3650);
				}
				emitter.emit("docking:layoutChanged");
			});
		},

		async renderLiveMode() {
			let layoutLoaded = false;
			const serializedLayout = getCookie("liveModeLayout");
			if (serializedLayout) {
				try {
					const {layout, selectedStation} = JSON.parse(serializedLayout);
					if (selectedStation.OrganizationID === this.orgID) {
						await this.$store.dispatch("getStation", selectedStation).then(station => {
							this.$store.commit("setLiveModeSelectedStationID", station.StationID);
						});
					}
					this.dockViewApi.fromJSON(layout);
					layoutLoaded = true;
				} catch (err) {
					// log the error
				}
			}

			if (!layoutLoaded) {
				//Render the default layout
				const onAirPanel = this.addPanel({name: "onAir"});
				const goLivePanel = this.addPanel({
					name: "goLive",
					extras: {
						position: {
							referencePanel: onAirPanel,
							direction: "right"
						}
					}
				});
				const playlistPanel = this.addPanel({
					name: "playlist",
					extras: {
						position: {
							referencePanel: onAirPanel,
							direction: "below"
						}
					}
				});
				this.addPanel({
					name: "hotkeys",
					extras: {
						position: {
							referencePanel: goLivePanel,
							direction: "below"
						}
					}
				});

				onAirPanel.api.setSize({
					width: this.dockViewApi.width * 0.6, // desired width in pixels
					height: 325 // desired height in pixels
				});
				goLivePanel.api.setSize({
					height: 325 // desired height in pixels
				});
				playlistPanel.api.setSize({
					width: this.dockViewApi.width * 0.6 // desired width in pixels
				});
			}
		},

		renderOfflineMode() {
			let layoutLoaded = false;
			const serializedLayout = getCookie("offlineModeLayout");
			if (serializedLayout) {
				try {
					const {layout} = JSON.parse(serializedLayout);
					this.dockViewApi.fromJSON(layout);
					layoutLoaded = true;
				} catch (err) {
					// log the error
				}
			}

			if (!layoutLoaded) {
				const onAirPanel = this.addPanel({name: "onAir"});
				this.addPanel({
					name: "hotkeys",
					extras: {
						position: {
							referencePanel: onAirPanel,
							direction: "right"
						}
					}
				});
				const playlistPanel = this.addPanel({
					name: "playlist",
					extras: {
						position: {
							referencePanel: onAirPanel,
							direction: "below"
						}
					}
				});

				onAirPanel.api.setSize({
					width: this.dockViewApi.width * 0.6, // desired width in pixels
					height: 325 // desired height in pixels
				});
				playlistPanel.api.setSize({
					width: this.dockViewApi.width * 0.6 // desired width in pixels
				});
			}
		},

		addPanel({name, floating = false, popout = false, extras = {}, selectedStationID}) {
			const panel = Panels[name];
			const panelId = `${panel.component}-${Math.random().toString(16).substring(2)}`;

			const newPanel = this.dockViewApi.addPanel({
				id: panelId,
				component: panel.component,
				title:
					this.liveMode && this.liveModeSelectedStation
						? `${panel.title} - ${this.liveModeSelectedStation.StationName}`
						: panel.title,
				...(floating && {
					floating: {
						position: {left: 10, top: 10},
						width: panel.floating?.width ?? 800,
						height: panel.floating?.height ?? 800
					}
				}),
				...extras,
				params: {
					panelName: name,
					...(selectedStationID && {selectedStationID}),
					...(panel.feature && {panelFeature: panel.feature})
				}
			});

			if (popout) {
				this.popoutPanel(newPanel);
			}

			return newPanel;
		},

		popoutPanel(panel) {
			// Save the current panel state in sessionStorage so it can be restored if popped out on refresh
			sessionStorage.setItem(
				`popoutPanel-${panel.id}`,
				JSON.stringify({
					id: panel.id,
					component: panel.api.component,
					params: panel.params
				})
			);

			// Open the panel in its own separate pop-out (browser) window
			// Passing the panel (instead of its group) ensures only this panel is popped out
			// into a brand-new group rather than reusing/moving the existing group.
			this.dockViewApi.addPopoutGroup(panel, {
				popoutUrl: `?panel=${panel.id}`,
				// The panel's own component instance (e.g. MidiUI) keeps running in this
				// (main) window's JS realm even once its DOM is moved into the popout - so
				// code inside it that needs to resize the actual popup (e.g. to fit the
				// panel's configured floating width/height) can't just call window.resizeTo,
				// since the bare `window` there resolves to this window, not the popup's.
				// Hand it the real popup Window object via this event instead.
				onDidOpen: ({window: popoutWindow}) => {
					emitter.emit("midiPopoutWindowOpened", popoutWindow);
				}
			});
		},

		replacePanel({oldPanel, panel}) {
			const group = oldPanel.group;

			const newPanel = this.addPanel({
				name: panel,
				extras: {
					position: {
						referenceGroup: group.id,
						direction: "within"
					}
				},
				selectedStationID: oldPanel.params.selectedStationID
			});

			newPanel.api.setActive();
			this.dockViewApi.removePanel(oldPanel);
		},

		closePanel(panel) {
			this.dockViewApi.removePanel(panel);
		},

		cleanUpPopoutPanelsFromMainWindowSessionStorage() {
			const layout = this.dockViewApi.toJSON();
			const popoutPanels = layout.popoutGroups?.map(pg => pg.data.activeView) ?? [];

			Object.keys(sessionStorage).forEach(key => {
				const panelID = key.split("popoutPanel-")[1];
				if (panelID && popoutPanels.includes(panelID)) {
					sessionStorage.removeItem(key);
				}
			});
		}
	}
};
</script>

<style lang="scss" scoped>
@keyframes pulse {
	0% {
		background-color: red;
	}
	50% {
		background-color: yellow;
	}
	100% {
		background-color: red;
	}
}

:deep(#dockview-container) {
	height: 100%;
	width: 100%;

	&.alternateColors {
		> div,
		.dv-dockview {
			animation: pulse 1s infinite;
		}
	}

	> .dv-drop-target {
		> .dv-drop-target-dropzone {
			.dv-drop-target-selection {
				&.dv-drop-target-left,
				&.dv-drop-target-right {
					width: 10% !important;
				}

				&.dv-drop-target-right {
					left: 90% !important;
				}

				&.dv-drop-target-bottom,
				&.dv-drop-target-top {
					height: 20% !important;
				}

				&.dv-drop-target-bottom {
					top: 80% !important;
				}
			}
		}
	}
}
</style>
